import { useEffect, useRef } from 'react';
import { Card, CardContent, Box, Divider } from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type {
  UserSearchHit,
  UserDevice,
  UserDeliveriesResponse,
  UserUpdate,
  LocationMatch,
} from '@citibox-panel/api-client';
import type { SearchTab } from '../../search-shell/model/shellState';
import { useAccount } from '../../search-shell/hooks/useAccount';
import { useUserDevice } from '../hooks/useUserDevice';
import { accountTypeClass } from '../lib/accountType';
import { DeviceLayout } from './DeviceLayout';
import { EditAccountSection } from './EditAccountSection';
import { AccountTypeRow } from './AccountTypeRow';
import { DeliveriesLayout } from '../../user-deliveries/components/DeliveriesLayout';

export interface UserDetailCardProps {
  search: SearchTab;
  searchUsers: (phone: string) => Promise<UserSearchHit[]>;
  fetchDevice: (id: string) => Promise<UserDevice | null>;
  fetchDeliveries: (id: string) => Promise<UserDeliveriesResponse>;
  // A courier account's baja also carries the agent's reason.
  deleteUser: (id: string, amazonExclusion: boolean, reason?: string) => Promise<void>;
  updateUser: (id: string, changes: UserUpdate) => Promise<void>;
  // The sanction: the account alone on a «Usuario», plus the agent's reason on a courier account.
  blockUser: (id: string, reason?: string) => Promise<void>;
  // Lifts the sanction, on a courier account only, always with the agent's reason.
  unblockUser: (id: string, reason: string) => Promise<void>;
  // Feeds the address combobox of the edit mode. The SAME read the header's address search uses,
  // and cached under the same key, so the two screens share one answer.
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  // The tab's label is the titular's name and the host owns tabs, so the rename is reported up
  // rather than resolved here — which also keeps this slice testable without a router.
  onRenamed?: (name: string) => void;
  // The screen's live region belongs to `apps/shell` (CBX-11386); this slice only words what to say.
  onAnnounce?: (message: string) => void;
}

// The single «Usuario» card: account + device (left column) and the deliveries list
// (right column) share one border but each section still loads and fails on its own.
// Owns the account and device queries so the device query can key off the account's
// USER ID and the view-event fires once the device settles; the deliveries section owns
// its own query. The presentational sections only render the state they are handed.
export function UserDetailCard({
  search,
  searchUsers,
  fetchDevice,
  fetchDeliveries,
  deleteUser,
  updateUser,
  blockUser,
  unblockUser,
  searchLocations,
  onRenamed,
  onAnnounce,
}: UserDetailCardProps) {
  const { account, isPending: accountPending } = useAccount(search, searchUsers);
  const userId = account?.id;
  const accountType = account?.accountType ?? null;
  const { device, isPending: devicePending, isError, refetch } = useUserDevice(userId, fetchDevice);

  // Emit once per «Usuario», after the device query settles, with a categorical device_present and
  // account_type and no PII. A retry does not re-emit (same userId).
  const emittedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (userId === undefined || devicePending) return;
    if (emittedFor.current === userId) return;
    emittedFor.current = userId;
    const devicePresent = isError ? 'error' : device === null ? 'false' : 'true';
    // The class and never the role's database name: no free text from the accounts table reaches
    // telemetry, and 'unknown' is what tells a role that never arrived apart from one that did.
    track('user_card_viewed', {
      device_present: devicePresent,
      account_type: accountTypeClass(accountType),
    });
  }, [userId, devicePending, isError, device, accountType]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, m: 2 }}>
      {/* No CardContent padding: each region pads its own content so the dividers
          run edge to edge of the card (the rounded overflow clips their ends). */}
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, maxWidth: 360 }}>
            {/* Above the account region and outside it, so neither the edit mode nor the account's
                own skeleton touches it; it renders nothing until the account has a role to show. */}
            <AccountTypeRow accountType={accountType} />
            <Box sx={{ p: 2 }}>
              {/* `EditAccountSection` wraps `UserLayout` rather than sitting in one of its slots:
                  the edit mode decides what goes in BOTH of them — the three field controls and
                  the button stack — and the «Dar de baja» button is one of the things it decides,
                  which is why that section is mounted from in there and no longer from here. */}
              <EditAccountSection
                account={account}
                isPending={accountPending}
                phoneKey={search.key}
                updateUser={updateUser}
                deleteUser={deleteUser}
                blockUser={blockUser}
                unblockUser={unblockUser}
                searchLocations={searchLocations}
                {...(onRenamed ? { onRenamed } : {})}
                {...(onAnnounce ? { onAnnounce } : {})}
              />
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <DeviceLayout
                device={device}
                isPending={devicePending}
                isError={isError}
                onRetry={refetch}
              />
            </Box>
          </Box>
          {/* Stretches to the full card height (flex default align-items: stretch). */}
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
          <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
          <Box sx={{ flex: 1, p: 2 }}>
            <DeliveriesLayout
              search={search}
              searchUsers={searchUsers}
              fetchDeliveries={fetchDeliveries}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
